"use client";

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { usePlaygroundInitialData } from "#/features/playground/context/PlaygroundInitialDataContext";
import { usePlaygroundMobileLayout } from "#/features/playground/hooks/usePlaygroundMobileLayout";
import { api } from "#/shared/api";
import { useHasMounted } from "#/shared/hooks";
import { usePlaygroundRoute } from "#/shared/hooks/usePlaygroundRoute";
import { appendPlaygroundMobileViewQuery } from "#/shared/lib/appendPlaygroundMobileViewQuery";
import { buildCanonicalPlaygroundSlug, playgroundSlugKey } from "#/shared/lib/buildCanonicalPlaygroundSlug";
import { getRestorablePlaygroundPath } from "#/shared/lib/playgroundLastPath";
import { buildPlaygroundPath } from "#/shared/lib/playgroundRoute";
import { getLastPlaygroundPath } from "#/shared/local-storage/playgroundPath";

/**
 * Mirrors server canonical redirects for client navigations (instant nav, <Link>).
 * Server `redirect()` does not always update the browser URL during soft navigations.
 */
export const useClientCanonicalPlaygroundRedirect = () => {
  const $ = _c(40);
  const route = usePlaygroundRoute();
  const searchParams = useSearchParams();
  const isMobile = usePlaygroundMobileLayout();
  const serverInitialData = usePlaygroundInitialData();
  const hasMounted = useHasMounted();
  const redirectingRef = useRef(false);
  const routeProjectSlug = route?.slug[0] ?? "";
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get("view") ?? null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const viewParam = t0;
  const routePath = route?.pathname ?? "";
  const t1 = serverInitialData?.allBrief;
  const t2 = Boolean(route && !routeProjectSlug && viewParam !== "browse");
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      initialData: t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const allBriefQuery = api.project.allBrief.useQuery(undefined, t3);
  const t4 = Boolean(route && routeProjectSlug);
  const t5 = serverInitialData?.projectBySlug?.slug === routeProjectSlug ? serverInitialData.projectBySlug : undefined;
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = {
      enabled: t4,
      initialData: t5
    };
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const projectQuery = api.project.getBySlug.useQuery(routeProjectSlug, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      redirectingRef.current = false;
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== routePath) {
    t8 = [routePath];
    $[9] = routePath;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] !== allBriefQuery.data?.[0]?.slug || $[12] !== hasMounted || $[13] !== isMobile || $[14] !== route || $[15] !== routePath || $[16] !== routeProjectSlug || $[17] !== viewParam) {
    t9 = () => {
      if (!route || routeProjectSlug || viewParam === "browse" || redirectingRef.current || !hasMounted) {
        return;
      }
      const restoredPath = getRestorablePlaygroundPath(getLastPlaygroundPath(), route.basePath);
      if (restoredPath) {
        const restoredTarget = appendPlaygroundMobileViewQuery(restoredPath, {
          isMobile,
          hasViewParam: Boolean(viewParam),
          hasCanonicalSlug: true
        });
        if (restoredTarget !== routePath) {
          redirectingRef.current = true;
          route.navigateTo(restoredTarget, {
            replace: true
          });
        }
        return;
      }
      const firstProjectSlug = allBriefQuery.data?.[0]?.slug;
      if (!firstProjectSlug) {
        return;
      }
      redirectingRef.current = true;
      const targetPath = buildPlaygroundPath(route.basePath, [firstProjectSlug]);
      const pathWithMobileView = appendPlaygroundMobileViewQuery(targetPath, {
        isMobile,
        hasViewParam: Boolean(viewParam),
        hasCanonicalSlug: true
      });
      route.navigateTo(pathWithMobileView, {
        replace: true
      });
    };
    $[11] = allBriefQuery.data?.[0]?.slug;
    $[12] = hasMounted;
    $[13] = isMobile;
    $[14] = route;
    $[15] = routePath;
    $[16] = routeProjectSlug;
    $[17] = viewParam;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== allBriefQuery.data || $[20] !== hasMounted || $[21] !== isMobile || $[22] !== route || $[23] !== routePath || $[24] !== routeProjectSlug || $[25] !== viewParam) {
    t10 = [allBriefQuery.data, hasMounted, isMobile, route, routePath, routeProjectSlug, viewParam];
    $[19] = allBriefQuery.data;
    $[20] = hasMounted;
    $[21] = isMobile;
    $[22] = route;
    $[23] = routePath;
    $[24] = routeProjectSlug;
    $[25] = viewParam;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  useEffect(t9, t10);
  let t11;
  if ($[27] !== isMobile || $[28] !== projectQuery.data || $[29] !== route || $[30] !== routeProjectSlug || $[31] !== viewParam) {
    t11 = () => {
      if (!route || !routeProjectSlug || !projectQuery.data || redirectingRef.current) {
        return;
      }
      const canonicalSlug = buildCanonicalPlaygroundSlug(projectQuery.data, route.slug[1], route.slug[2]);
      if (playgroundSlugKey(route.slug) === playgroundSlugKey(canonicalSlug)) {
        redirectingRef.current = false;
        return;
      }
      redirectingRef.current = true;
      const canonicalPath = buildPlaygroundPath(route.basePath, canonicalSlug);
      const targetPath_0 = appendPlaygroundMobileViewQuery(canonicalPath, {
        isMobile,
        hasViewParam: Boolean(viewParam),
        hasCanonicalSlug: canonicalSlug.length > 0
      });
      route.navigateTo(targetPath_0, {
        replace: true
      });
    };
    $[27] = isMobile;
    $[28] = projectQuery.data;
    $[29] = route;
    $[30] = routeProjectSlug;
    $[31] = viewParam;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== isMobile || $[34] !== projectQuery.data || $[35] !== route || $[36] !== routePath || $[37] !== routeProjectSlug || $[38] !== viewParam) {
    t12 = [isMobile, projectQuery.data, route, routeProjectSlug, routePath, viewParam];
    $[33] = isMobile;
    $[34] = projectQuery.data;
    $[35] = route;
    $[36] = routePath;
    $[37] = routeProjectSlug;
    $[38] = viewParam;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  useEffect(t11, t12);
};